import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Buildings, Check, Flask } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | accepting | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    axios.get(`${API}/invite/${token}`)
      .then((res) => { setInvite(res.data); setStatus("ready"); })
      .catch((err) => {
        setErrorMsg(err.response?.data?.detail || "Invitation not found or expired");
        setStatus("error");
      });
  }, [token]);

  const handleAccept = async () => {
    setStatus("accepting");
    try {
      await axios.post(`${API}/invite/${token}/accept`, {}, { withCredentials: true });
      await checkAuth();
      setStatus("done");
      setTimeout(() => navigate("/dashboard", { replace: true }), 2500);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Failed to accept invitation");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6" data-testid="invite-accept-page">
      {status === "loading" && (
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#2D6A4F] animate-breathe mx-auto mb-4" />
          <p className="text-sm text-[#78857F]">Loading invitation...</p>
        </div>
      )}

      {status === "ready" && invite && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6">
            <Buildings size={32} weight="duotone" className="text-[#2D6A4F]" />
          </div>

          <h1 className="font-['Outfit'] text-2xl font-medium text-[#1A2421] mb-2">
            You're Invited
          </h1>
          <p className="text-sm text-[#78857F] mb-1">
            <span className="font-semibold text-[#1A2421]">{invite.org_name}</span> has invited you to join their wellness program on Mood Chemistry Lab.
          </p>
          <p className="text-xs text-[#78857F] mb-4">{invite.email}</p>

          {invite.message && (
            <div className="card-neuro p-4 mb-6 text-left">
              <p className="text-xs text-[#78857F] mb-1">Personal message:</p>
              <p className="text-sm text-[#1A2421] italic">"{invite.message}"</p>
            </div>
          )}

          <div className="card-neuro p-5 mb-6 text-left">
            <p className="text-xs font-semibold text-[#2D6A4F] mb-2">What you get:</p>
            {["Full premium access included", "AI-powered journal reflections", "Advanced mood tracking & insights", "Guided sensory reset sessions"].map((f, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <Check size={14} weight="bold" className="text-[#2D6A4F]" />
                <span className="text-sm text-[#1A2421]">{f}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleAccept}
            data-testid="accept-invite-button"
            className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium shadow-md hover:-translate-y-0.5 transition-all"
          >
            Accept & Join
          </Button>
        </motion.div>
      )}

      {status === "accepting" && (
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#2D6A4F] animate-breathe mx-auto mb-4" />
          <p className="text-sm text-[#78857F]">Setting up your account...</p>
        </div>
      )}

      {status === "done" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-16 h-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4"
          >
            <Check size={32} weight="bold" className="text-[#2D6A4F]" />
          </motion.div>
          <h2 className="font-['Outfit'] text-xl font-medium text-[#1A2421] mb-1">You're in!</h2>
          <p className="text-sm text-[#78857F]">Redirecting to your dashboard...</p>
        </motion.div>
      )}

      {status === "error" && (
        <div className="text-center max-w-md">
          <Flask size={32} weight="duotone" className="text-[#E76F51] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-xl font-medium text-[#1A2421] mb-2">Invitation Issue</h2>
          <p className="text-sm text-[#78857F] mb-4">{errorMsg}</p>
          <Button onClick={() => navigate("/login")} variant="outline" className="rounded-full border-[#E1D9CD]">
            Go to Login
          </Button>
        </div>
      )}
    </div>
  );
}
